// Parser for <KnowledgeDoc> tags emitted by the agent in both Ask and Agent mode.
// Kept separate from parseArtifacts() on purpose: knowledge docs are a distinct,
// Markdown-only concept persisted to the `knowledge_docs` collection, and the
// dedicated tag lets us route them independently of artifacts.

export interface KnowledgeDoc {
  identifier: string; // stable, topic-derived id from the `id` attribute (groups versions)
  title: string;
  version?: string; // "1", "2", "3" ...
  content: string; // Markdown
  raw?: string;
  isProcessing?: boolean; // still streaming
}

export function formatKnowledgeDocTitle(title: string, version?: string): string {
  return version ? `${title} - V${version}` : title;
}

function extractAttrs(attrString: string): Record<string, string> {
  const attrs: Record<string, string> = {};
  const attrMatches = attrString.match(/(\w+)="([^"]*)"/g);
  if (attrMatches) {
    attrMatches.forEach(attr => {
      const eq = attr.indexOf('=');
      const key = attr.slice(0, eq);
      const value = attr.slice(eq + 1).replace(/"/g, '');
      if (key) attrs[key] = value;
    });
  }
  return attrs;
}

export function parseKnowledgeDocs(
  message: string,
  isStreaming: boolean = false,
): { parts: (string | KnowledgeDoc)[] } {
  const regex = /<KnowledgeDoc\s+([^>]+)>([\s\S]*?)<\/KnowledgeDoc>/gi;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  const parts: (string | KnowledgeDoc)[] = [];

  while ((match = regex.exec(message)) !== null) {
    if (match.index > lastIndex) {
      parts.push(message.slice(lastIndex, match.index));
    }
    const attrs = extractAttrs(match[1]);
    parts.push({
      identifier: attrs.id || attrs.identifier || '',
      title: attrs.title || 'Knowledge Document',
      version: attrs.version,
      content: match[2].trim(),
      raw: match[0],
    });
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < message.length) {
    parts.push(message.slice(lastIndex));
  }

  // Handle an in-progress (unclosed) <KnowledgeDoc ...> tag while streaming so the
  // raw tag and partial Markdown don't flash as plain text.
  const finalParts: (string | KnowledgeDoc)[] = [];
  for (const part of parts) {
    if (typeof part !== 'string') {
      finalParts.push(part);
      continue;
    }
    const incomplete = part.match(/<KnowledgeDoc\s+([^>]+)>([\s\S]*?)$/i);
    if (incomplete) {
      const attrs = extractAttrs(incomplete[1]);
      const content = incomplete[2] || '';
      const before = part.slice(0, incomplete.index);
      if (before) finalParts.push(before);
      finalParts.push({
        identifier: attrs.id || attrs.identifier || '',
        title: attrs.title || 'Generating Knowledge Document...',
        version: attrs.version,
        content,
        raw: incomplete[0],
        isProcessing: isStreaming,
      });
    } else {
      finalParts.push(part);
    }
  }

  return { parts: finalParts };
}
